import { useEffect, useMemo, useState } from 'react';
import {
  Drawer,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Chip,
  Stack,
  Typography,
  Alert,
  IconButton,
  Tooltip,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Box,
  Toolbar,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { generateSmartPromotionCopy } from '../../api/smartPromotionsApi';

const CHANNELS = ['inApp', 'push', 'concierge'];
const TIME_BUCKETS = ['morning', 'midday', 'earlyEvening', 'lateNight'];
const STAY_PHASES = ['preCheckin', 'duringStay', 'preCheckout', 'postCheckout'];
const CHANNEL_LABELS = {
  inApp: 'In-App',
  push: 'Push',
  concierge: 'Concierge',
};
const STAY_PHASE_LABELS = {
  preCheckin: 'Before Check-In',
  duringStay: 'During Stay',
  preCheckout: 'Before Check-Out',
  postCheckout: 'After Check-Out',
};

const MINUTES_UNIT = 'minutes';
const HOURS_UNIT = 'hours';

const TARGET_CATEGORY_CONFIG = [
  { value: 'services', label: 'Services', types: ['service'] },
  { value: 'restaurants', label: 'Restaurants', types: ['restaurant'] },
  { value: 'restaurantMenuItems', label: 'Restaurant Menu Items', types: ['restaurantMenuItem'] },
  { value: 'spas', label: 'Spas', types: ['spa'] },
  { value: 'spaTreatments', label: 'Spa Treatments', types: ['spaTreatment'] },
];

const TARGET_CATEGORY_BY_TYPE = TARGET_CATEGORY_CONFIG.reduce((acc, config) => {
  config.types.forEach((type) => {
    acc[type] = config.value;
  });
  return acc;
}, {});

function buildTargetOptions({ services = [], restaurants = [], spas = [] }) {
  const options = [];

  services.forEach((svc) => {
    options.push({
      label: svc.title || svc.name || 'Untitled service',
      subtitle: svc.category || '',
      targetType: 'service',
      targetId: svc._id,
      propertyId: svc.propertyId,
      category: TARGET_CATEGORY_BY_TYPE.service,
    });
  });

  restaurants.forEach((restaurant) => {
    options.push({
      label: restaurant.name || 'Restaurant',
      subtitle: restaurant.subtitle || restaurant.about || '',
      targetType: 'restaurant',
      targetId: restaurant._id,
      propertyId: restaurant.propertyId,
      category: TARGET_CATEGORY_BY_TYPE.restaurant,
    });
    if (Array.isArray(restaurant.menu)) {
      restaurant.menu.forEach((item) => {
        options.push({
          label: `${restaurant.name || 'Restaurant'} • ${item.name}`,
          subtitle: item.description || '',
          targetType: 'restaurantMenuItem',
          targetId: item._id,
          parentId: restaurant._id,
          propertyId: restaurant.propertyId,
          category: TARGET_CATEGORY_BY_TYPE.restaurantMenuItem,
        });
      });
    }
  });

  spas.forEach((spa) => {
    options.push({
      label: spa.name || 'Spa',
      subtitle: spa.subtitle || spa.about || '',
      targetType: 'spa',
      targetId: spa._id,
      propertyId: spa.propertyId,
      category: TARGET_CATEGORY_BY_TYPE.spa,
    });
    if (Array.isArray(spa.massages)) {
      spa.massages.forEach((massage) => {
        options.push({
          label: `${spa.name || 'Spa'} • ${massage.typeOfMassage}`,
          subtitle: massage.description || '',
          targetType: 'spaTreatment',
          targetId: massage._id,
          parentId: spa._id,
          propertyId: spa.propertyId,
          category: TARGET_CATEGORY_BY_TYPE.spaTreatment,
        });
      });
    }
  });

  return options;
}

export default function SmartPromotionModal({
  open,
  onClose,
  onSave,
  initialPromotion,
  services,
  restaurants,
  spas,
  propertyName,
}) {
  const baseOptions = useMemo(
    () => buildTargetOptions({ services, restaurants, spas }),
    [services, restaurants, spas]
  );

  const targetOptions = useMemo(() => {
    if (!initialPromotion) return baseOptions;
    const exists = baseOptions.some(
      (opt) =>
        opt.targetType === initialPromotion.targetType &&
        opt.targetId === initialPromotion.targetId &&
        (opt.parentId || null) === (initialPromotion.parentId || null)
    );
    if (exists) return baseOptions;
    return [
      ...baseOptions,
      {
        label: initialPromotion.title || 'Selected item',
        subtitle: initialPromotion.subtitle || '',
        targetType: initialPromotion.targetType,
        targetId: initialPromotion.targetId,
        parentId: initialPromotion.parentId,
        propertyId: initialPromotion.propertyId,
        category: TARGET_CATEGORY_BY_TYPE[initialPromotion.targetType],
      },
    ];
  }, [baseOptions, initialPromotion]);

  const categoryOptions = useMemo(
    () => buildCategoryOptions(targetOptions),
    [targetOptions]
  );

  const defaultSelection = useMemo(() => {
    if (!initialPromotion) return null;
    return targetOptions.find(
      (opt) =>
        opt.targetType === initialPromotion.targetType &&
        opt.targetId === initialPromotion.targetId &&
        (opt.parentId || null) === (initialPromotion.parentId || null)
    );
  }, [initialPromotion, targetOptions]);

  const leadTimeInitial = useMemo(
    () => normalizeTimeValue(initialPromotion?.promotionRules?.leadTimeMinutes),
    [initialPromotion?.promotionRules?.leadTimeMinutes]
  );
  const checkinLeadInitial = useMemo(
    () => normalizeTimeValue(initialPromotion?.promotionRules?.checkinLeadMinutes),
    [initialPromotion?.promotionRules?.checkinLeadMinutes]
  );
  const checkoutLeadInitial = useMemo(
    () => normalizeTimeValue(initialPromotion?.promotionRules?.checkoutLeadMinutes),
    [initialPromotion?.promotionRules?.checkoutLeadMinutes]
  );
  const postCheckoutInitial = useMemo(
    () => normalizeTimeValue(initialPromotion?.promotionRules?.postCheckoutWindowMinutes),
    [initialPromotion?.promotionRules?.postCheckoutWindowMinutes]
  );

  const [selectedTarget, setSelectedTarget] = useState(defaultSelection);
  const [selectedCategory, setSelectedCategory] = useState(() =>
    defaultSelection ? TARGET_CATEGORY_BY_TYPE[defaultSelection.targetType] : ''
  );
  const [status, setStatus] = useState(initialPromotion?.promotionRules?.status || 'active');
  const [priority, setPriority] = useState(
    initialPromotion?.promotionRules?.priority ?? initialPromotion?.priority ?? 0
  );
  const [channels, setChannels] = useState(() => {
    const rule = initialPromotion?.promotionRules;
    if (!rule?.channels) return CHANNELS;
    return CHANNELS.filter((ch) => rule.channels[ch] !== false);
  });
  const [stayPhases, setStayPhases] = useState(initialPromotion?.promotionRules?.stayPhases || []);
  const includesPreCheckin = useMemo(() => stayPhases.includes('preCheckin'), [stayPhases]);
  const includesPreCheckout = useMemo(() => stayPhases.includes('preCheckout'), [stayPhases]);
  const includesPostCheckout = useMemo(() => stayPhases.includes('postCheckout'), [stayPhases]);
  const [timeBuckets, setTimeBuckets] = useState(
    initialPromotion?.promotionRules?.timeBuckets || []
  );
  const [notes, setNotes] = useState(initialPromotion?.promotionRules?.notes || '');
  const [leadTime, setLeadTime] = useState(leadTimeInitial.value);
  const [leadTimeUnit, setLeadTimeUnit] = useState(leadTimeInitial.unit);
  const [checkinLead, setCheckinLead] = useState(checkinLeadInitial.value);
  const [checkinLeadUnit, setCheckinLeadUnit] = useState(checkinLeadInitial.unit);
  const [checkoutLead, setCheckoutLead] = useState(checkoutLeadInitial.value);
  const [checkoutLeadUnit, setCheckoutLeadUnit] = useState(checkoutLeadInitial.unit);
  const [postCheckoutWindow, setPostCheckoutWindow] = useState(postCheckoutInitial.value);
  const [postCheckoutUnit, setPostCheckoutUnit] = useState(postCheckoutInitial.unit);
  useEffect(() => {
    if (!includesPreCheckin) {
      setCheckinLead('');
      setCheckinLeadUnit(MINUTES_UNIT);
    }
  }, [includesPreCheckin]);
  useEffect(() => {
    if (!includesPreCheckout) {
      setCheckoutLead('');
      setCheckoutLeadUnit(MINUTES_UNIT);
    }
  }, [includesPreCheckout]);
  useEffect(() => {
    if (!includesPostCheckout) {
      setPostCheckoutWindow('');
      setPostCheckoutUnit(MINUTES_UNIT);
    }
  }, [includesPostCheckout]);
  const [audience, setAudience] = useState(
    initialPromotion?.promotionRules?.audienceTags?.join(', ') || ''
  );
  const [upsellCopy, setUpsellCopy] = useState(initialPromotion?.promotionRules?.upsellCopy || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [suggestions, setSuggestions] = useState([]);

  useEffect(() => {
    if (!open) return;
    const fallbackTarget = initialPromotion
      ? {
          targetType: initialPromotion.targetType,
          targetId: initialPromotion.targetId,
          parentId: initialPromotion.parentId,
          propertyId: initialPromotion.propertyId,
        }
      : null;
    const resolvedSelection = defaultSelection || fallbackTarget;
    setSelectedTarget(resolvedSelection);
    setStatus(initialPromotion?.promotionRules?.status || 'active');
    setPriority(initialPromotion?.promotionRules?.priority ?? initialPromotion?.priority ?? 0);
    setChannels(() => {
      const rule = initialPromotion?.promotionRules;
      if (!rule?.channels) return CHANNELS;
      return CHANNELS.filter((ch) => rule.channels[ch] !== false);
    });
    setStayPhases(initialPromotion?.promotionRules?.stayPhases || []);
    setTimeBuckets(initialPromotion?.promotionRules?.timeBuckets || []);
    setNotes(initialPromotion?.promotionRules?.notes || '');
    applyNormalizedTime(
      setLeadTime,
      setLeadTimeUnit,
      initialPromotion?.promotionRules?.leadTimeMinutes
    );
    applyNormalizedTime(
      setCheckinLead,
      setCheckinLeadUnit,
      initialPromotion?.promotionRules?.checkinLeadMinutes
    );
    applyNormalizedTime(
      setCheckoutLead,
      setCheckoutLeadUnit,
      initialPromotion?.promotionRules?.checkoutLeadMinutes
    );
    applyNormalizedTime(
      setPostCheckoutWindow,
      setPostCheckoutUnit,
      initialPromotion?.promotionRules?.postCheckoutWindowMinutes
    );
    setAudience(initialPromotion?.promotionRules?.audienceTags?.join(', ') || '');
    setUpsellCopy(initialPromotion?.promotionRules?.upsellCopy || '');
    setSuggestions([]);
    setError(null);
    setSelectedCategory(() => {
      if (defaultSelection) {
        return TARGET_CATEGORY_BY_TYPE[defaultSelection.targetType] || '';
      }
      const firstAvailable = categoryOptions[0]?.value;
      return firstAvailable || '';
    });
  }, [open, defaultSelection, initialPromotion, categoryOptions]);

  const resetState = () => {
    const fallbackTarget = initialPromotion
      ? {
          targetType: initialPromotion.targetType,
          targetId: initialPromotion.targetId,
          parentId: initialPromotion.parentId,
          propertyId: initialPromotion.propertyId,
        }
      : null;
    const resolvedSelection = defaultSelection || fallbackTarget;
    setSelectedTarget(resolvedSelection);
    setStatus(initialPromotion?.promotionRules?.status || 'active');
    setPriority(initialPromotion?.promotionRules?.priority ?? initialPromotion?.priority ?? 0);
    setChannels(() => {
      const rule = initialPromotion?.promotionRules;
      if (!rule?.channels) return CHANNELS;
      return CHANNELS.filter((ch) => rule.channels[ch] !== false);
    });
    setStayPhases(initialPromotion?.promotionRules?.stayPhases || []);
    setTimeBuckets(initialPromotion?.promotionRules?.timeBuckets || []);
    setNotes(initialPromotion?.promotionRules?.notes || '');
    applyNormalizedTime(
      setLeadTime,
      setLeadTimeUnit,
      initialPromotion?.promotionRules?.leadTimeMinutes
    );
    applyNormalizedTime(
      setCheckinLead,
      setCheckinLeadUnit,
      initialPromotion?.promotionRules?.checkinLeadMinutes
    );
    applyNormalizedTime(
      setCheckoutLead,
      setCheckoutLeadUnit,
      initialPromotion?.promotionRules?.checkoutLeadMinutes
    );
    applyNormalizedTime(
      setPostCheckoutWindow,
      setPostCheckoutUnit,
      initialPromotion?.promotionRules?.postCheckoutWindowMinutes
    );
    setAudience(initialPromotion?.promotionRules?.audienceTags?.join(', ') || '');
    setUpsellCopy(initialPromotion?.promotionRules?.upsellCopy || '');
    setSuggestions([]);
    setError(null);
    setSelectedCategory(() => {
      if (resolvedSelection) {
        return TARGET_CATEGORY_BY_TYPE[resolvedSelection.targetType] || '';
      }
      const firstAvailable = categoryOptions[0]?.value;
      return firstAvailable || '';
    });
  };

  const handleLeadTimeUnitChange = (_, nextUnit) => {
    if (!nextUnit || nextUnit === leadTimeUnit) return;
    setLeadTime((prev) => convertDisplayedValue(prev, leadTimeUnit, nextUnit));
    setLeadTimeUnit(nextUnit);
  };

  const handleCheckinUnitChange = (_, nextUnit) => {
    if (!nextUnit || nextUnit === checkinLeadUnit) return;
    setCheckinLead((prev) => convertDisplayedValue(prev, checkinLeadUnit, nextUnit));
    setCheckinLeadUnit(nextUnit);
  };

  const handleCheckoutUnitChange = (_, nextUnit) => {
    if (!nextUnit || nextUnit === checkoutLeadUnit) return;
    setCheckoutLead((prev) => convertDisplayedValue(prev, checkoutLeadUnit, nextUnit));
    setCheckoutLeadUnit(nextUnit);
  };

  const handlePostCheckoutUnitChange = (_, nextUnit) => {
    if (!nextUnit || nextUnit === postCheckoutUnit) return;
    setPostCheckoutWindow((prev) => convertDisplayedValue(prev, postCheckoutUnit, nextUnit));
    setPostCheckoutUnit(nextUnit);
  };

  const handleClose = () => {
    if (saving) return;
    resetState();
    onClose();
  };

  const handleSave = async () => {
    if (!selectedTarget) {
      setError('Please select a service, restaurant, or spa item.');
      return;
    }
    const parsedLeadRaw = leadTime === '' ? undefined : Number(leadTime);
    if (leadTime !== '' && !Number.isFinite(parsedLeadRaw)) {
      setError('Minimum notice must be a valid number.');
      return;
    }

    let resolvedCheckinMinutes;
    if (includesPreCheckin) {
      if (checkinLead === '') {
        resolvedCheckinMinutes = undefined;
      } else {
        const numeric = Number(checkinLead);
        if (!Number.isFinite(numeric)) {
          setError('Check-in window must be a valid number.');
          return;
        }
        resolvedCheckinMinutes = numeric * (checkinLeadUnit === HOURS_UNIT ? 60 : 1);
      }
    }

    let resolvedCheckoutMinutes;
    if (includesPreCheckout) {
      if (checkoutLead === '') {
        resolvedCheckoutMinutes = undefined;
      } else {
        const numeric = Number(checkoutLead);
        if (!Number.isFinite(numeric)) {
          setError('Check-out window must be a valid number.');
          return;
        }
        resolvedCheckoutMinutes = numeric * (checkoutLeadUnit === HOURS_UNIT ? 60 : 1);
      }
    }

    let resolvedPostCheckoutMinutes;
    if (includesPostCheckout) {
      if (postCheckoutWindow === '') {
        resolvedPostCheckoutMinutes = undefined;
      } else {
        const numeric = Number(postCheckoutWindow);
        if (!Number.isFinite(numeric)) {
          setError('Post check-out window must be a valid number.');
          return;
        }
        resolvedPostCheckoutMinutes = numeric * (postCheckoutUnit === HOURS_UNIT ? 60 : 1);
      }
    }

    const resolvedLeadMinutes =
      leadTime === '' || parsedLeadRaw === undefined
        ? undefined
        : parsedLeadRaw * (leadTimeUnit === HOURS_UNIT ? 60 : 1);
    setSaving(true);
    setError(null);
    try {
      const promotionRules = {
        status,
        priority: Number(priority) || 0,
        channels: {
          inApp: channels.includes('inApp'),
          push: channels.includes('push'),
          concierge: channels.includes('concierge'),
        },
        stayPhases,
        checkinLeadMinutes:
          includesPreCheckin ? resolvedCheckinMinutes : undefined,
        checkoutLeadMinutes:
          includesPreCheckout ? resolvedCheckoutMinutes : undefined,
        postCheckoutWindowMinutes:
          includesPostCheckout ? resolvedPostCheckoutMinutes : undefined,
        timeBuckets,
        notes: notes || undefined,
        leadTimeMinutes: resolvedLeadMinutes,
        audienceTags: audience
          ? audience
              .split(',')
              .map((t) => t.trim())
              .filter(Boolean)
          : undefined,
        upsellCopy: upsellCopy ? upsellCopy.trim() : undefined,
      };
      await onSave({
        ...selectedTarget,
        promotionRules,
      });
      resetState();
      onClose();
    } catch (err) {
      console.error('[SmartPromotionModal] save failed', err);
      setError(err?.message || 'Failed to save promotion');
    } finally {
      setSaving(false);
    }
  };

  const handleGenerate = async () => {
    if (!selectedTarget) {
      setError('Select a target before generating copy.');
      return;
    }
    setGenerating(true);
    setError(null);
    try {
      const copies = await generateSmartPromotionCopy({
        targetName: selectedTarget.label,
        targetType: selectedTarget.targetType,
        propertyName,
        timeBuckets,
      });
      if (Array.isArray(copies) && copies.length) {
        setUpsellCopy(copies[0]);
        setSuggestions(copies);
      } else {
        setSuggestions([]);
      }
    } catch (err) {
      console.error('[SmartPromotionModal] generate copy failed', err);
      setError(err?.message || 'Failed to generate keywords');
    } finally {
      setGenerating(false);
    }
  };

  const handleDisable = async () => {
    if (!selectedTarget) return;
    setSaving(true);
    setError(null);
    try {
      await onSave({
        ...selectedTarget,
        promotionRules: null,
      });
      resetState();
      onClose();
    } catch (err) {
      console.error('[SmartPromotionModal] disable failed', err);
      setError(err?.message || 'Failed to disable promotion');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer anchor="right" open={open} onClose={handleClose} PaperProps={{ sx: { width: { xs: '100%', sm: 560 } } }}>
      <Toolbar sx={{ justifyContent: 'space-between', px: 3 }}>
        <Typography variant="h6">
          {initialPromotion ? 'Edit Smart Promotion' : 'Add Smart Promotion'}
        </Typography>
        <Button onClick={handleClose} color="inherit">
          Close
        </Button>
      </Toolbar>
      <Divider />
      <Box sx={{ px: 3, py: 2, display: 'flex', flexDirection: 'column', height: '100%', overflow: 'auto' }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Stack spacing={3}>
          <Accordion defaultExpanded sx={{ borderRadius: 2, boxShadow: 'none', border: 1, borderColor: 'divider' }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography variant="subtitle1">Target & Basics</Typography>
                <Typography variant="body2" color="text.secondary">
                  Choose what to promote and how prominently it should appear.
                </Typography>
              </Box>
            </AccordionSummary>
            <Divider />
            <AccordionDetails>
              <Stack spacing={2}>
                <Stack spacing={2}>
                  <TextField
                    select
                    fullWidth
                    label="Target Category"
                    value={selectedCategory}
                    disabled={categoryOptions.length === 0}
                    onChange={(e) => {
                      const nextCategory = e.target.value;
                      setSelectedCategory(nextCategory);
                      if (!nextCategory) {
                        setSelectedTarget(null);
                        return;
                      }
                      const nextOptions = targetOptions.filter((opt) => opt.category === nextCategory);
                      if (!nextOptions.length) {
                        setSelectedTarget(null);
                        return;
                      }
                      const existsInCategory = nextOptions.some((opt) => optionKey(opt) === optionKey(selectedTarget));
                      if (!existsInCategory) {
                        setSelectedTarget(nextOptions[0]);
                      }
                    }}
                  >
                    {categoryOptions.map((category) => (
                      <MenuItem key={category.value} value={category.value}>
                        {category.label}
                      </MenuItem>
                    ))}
                  </TextField>

                  <TextField
                    select
                    fullWidth
                    label="Select Target"
                    value={selectedTarget ? optionKey(selectedTarget) : ''}
                    onChange={(e) => {
                      const next = targetOptions.find((opt) => optionKey(opt) === e.target.value);
                      setSelectedTarget(next || null);
                    }}
                    disabled={!selectedCategory}
                  >
                    {targetOptions
                      .filter((opt) => !selectedCategory || opt.category === selectedCategory)
                      .map((opt) => (
                        <MenuItem key={optionKey(opt)} value={optionKey(opt)}>
                          <Stack spacing={0.5}>
                            <Typography variant="subtitle2">{opt.label}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {readableTargetType(opt.targetType)}
                              {opt.subtitle ? ` • ${opt.subtitle}` : ''}
                            </Typography>
                          </Stack>
                        </MenuItem>
                      ))}
                  </TextField>
                  {targetOptions.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                      No eligible services, restaurants, or spa items available. Create them first, then return to enable smart promotions.
                    </Typography>
                  )}
                  {selectedCategory && !targetOptions.some((opt) => opt.category === selectedCategory) && (
                    <Typography variant="body2" color="text.secondary">
                      No items found in the selected category.
                    </Typography>
                  )}
                </Stack>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select value={status} label="Status" onChange={(e) => setStatus(e.target.value)}>
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="paused">Paused</MenuItem>
                      <MenuItem value="draft">Draft</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    label="Priority"
                    type="number"
                    fullWidth
                    value={priority}
                    onChange={(e) => setPriority(e.target.value)}
                    helperText="Lower number = higher priority"
                  />
                </Stack>

                <FormControl fullWidth>
                  <InputLabel>Channels</InputLabel>
                  <Select
                    multiple
                    value={channels}
                    onChange={(e) => setChannels(e.target.value)}
                    input={<OutlinedInput label="Channels" />}
                    renderValue={(selected) => (
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        {selected.map((value) => (
                          <Chip key={value} label={CHANNEL_LABELS[value] || value} size="small" />
                        ))}
                      </Stack>
                    )}
                  >
                    {CHANNELS.map((channel) => (
                      <MenuItem key={channel} value={channel}>
                        <Checkbox checked={channels.indexOf(channel) > -1} />
                        <ListItemText primary={CHANNEL_LABELS[channel] || channel} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
            </AccordionDetails>
          </Accordion>

          <Accordion defaultExpanded sx={{ borderRadius: 2, boxShadow: 'none', border: 1, borderColor: 'divider' }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography variant="subtitle1">Stay Phases & Timing</Typography>
                <Typography variant="body2" color="text.secondary">
                  Control when promotions are eligible based on the guest lifecycle.
                </Typography>
              </Box>
            </AccordionSummary>
            <Divider />
            <AccordionDetails>
              <Stack spacing={2}>
                <FormControl fullWidth>
                  <InputLabel>Stay Phases</InputLabel>
                  <Select
                    multiple
                    value={stayPhases}
                    onChange={(e) => setStayPhases(e.target.value)}
                    input={<OutlinedInput label="Stay Phases" />}
                    renderValue={(selected) => (
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        {selected.map((value) => (
                          <Chip key={value} label={formatStayPhase(value)} size="small" />
                        ))}
                      </Stack>
                    )}
                  >
                    {STAY_PHASES.map((phase) => (
                      <MenuItem key={phase} value={phase}>
                        <Checkbox checked={stayPhases.indexOf(phase) > -1} />
                        <ListItemText primary={formatStayPhase(phase)} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {includesPreCheckin && (
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'flex-start' }}>
                    <TextField
                      label="Check-In Window"
                      type="number"
                      fullWidth
                      value={checkinLead}
                      onChange={(e) => setCheckinLead(e.target.value)}
                      helperText="Applies only to the before check-in phase; triggers once the guest is within this much time of the scheduled check-in."
                      sx={{ flexGrow: 1 }}
                    />
                    <ToggleButtonGroup
                      value={checkinLeadUnit}
                      exclusive
                      onChange={handleCheckinUnitChange}
                      size="small"
                      sx={{ mt: { xs: 0, sm: 2.5 } }}
                      aria-label="Check-in time unit"
                    >
                      <ToggleButton value={MINUTES_UNIT}>Min</ToggleButton>
                      <ToggleButton value={HOURS_UNIT}>Hr</ToggleButton>
                    </ToggleButtonGroup>
                  </Stack>
                )}

                {includesPreCheckout && (
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'flex-start' }}>
                    <TextField
                      label="Check-Out Window"
                      type="number"
                      fullWidth
                      value={checkoutLead}
                      onChange={(e) => setCheckoutLead(e.target.value)}
                      helperText="Applies only to the before check-out phase; triggers once the guest is within this much time of their scheduled check-out."
                      sx={{ flexGrow: 1 }}
                    />
                    <ToggleButtonGroup
                      value={checkoutLeadUnit}
                      exclusive
                      onChange={handleCheckoutUnitChange}
                      size="small"
                      sx={{ mt: { xs: 0, sm: 2.5 } }}
                      aria-label="Check-out time unit"
                    >
                      <ToggleButton value={MINUTES_UNIT}>Min</ToggleButton>
                      <ToggleButton value={HOURS_UNIT}>Hr</ToggleButton>
                    </ToggleButtonGroup>
                  </Stack>
                )}

                {includesPostCheckout && (
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'flex-start' }}>
                    <TextField
                      label="Post Check-Out Window"
                      type="number"
                      fullWidth
                      value={postCheckoutWindow}
                      onChange={(e) => setPostCheckoutWindow(e.target.value)}
                      helperText="Applies only to the after check-out phase; triggers while the guest remains within this much time after leaving."
                      sx={{ flexGrow: 1 }}
                    />
                    <ToggleButtonGroup
                      value={postCheckoutUnit}
                      exclusive
                      onChange={handlePostCheckoutUnitChange}
                      size="small"
                      sx={{ mt: { xs: 0, sm: 2.5 } }}
                      aria-label="Post check-out time unit"
                    >
                      <ToggleButton value={MINUTES_UNIT}>Min</ToggleButton>
                      <ToggleButton value={HOURS_UNIT}>Hr</ToggleButton>
                    </ToggleButtonGroup>
                  </Stack>
                )}

                <FormControl fullWidth>
                  <InputLabel>Time Buckets</InputLabel>
                  <Select
                    multiple
                    value={timeBuckets}
                    onChange={(e) => setTimeBuckets(e.target.value)}
                    input={<OutlinedInput label="Time Buckets" />}
                    renderValue={(selected) => (
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        {selected.map((value) => (
                          <Chip key={value} label={value.replace(/([A-Z])/g, ' $1')} size="small" />
                        ))}
                      </Stack>
                    )}
                  >
                    {TIME_BUCKETS.map((bucket) => (
                      <MenuItem key={bucket} value={bucket}>
                        <Checkbox checked={timeBuckets.indexOf(bucket) > -1} />
                        <ListItemText primary={bucket.replace(/([A-Z])/g, ' $1')} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'flex-start' }}>
                  <TextField
                    label="Minimum Notice"
                    type="number"
                    fullWidth
                    value={leadTime}
                    onChange={(e) => setLeadTime(e.target.value)}
                    helperText="Guests must be at least this much time away from the experience or event before the promotion applies."
                    sx={{ flexGrow: 1 }}
                  />
                  <ToggleButtonGroup
                    value={leadTimeUnit}
                    exclusive
                    onChange={handleLeadTimeUnitChange}
                    size="small"
                    sx={{ mt: { xs: 0, sm: 2.5 } }}
                    aria-label="Minimum notice time unit"
                  >
                    <ToggleButton value={MINUTES_UNIT}>Min</ToggleButton>
                    <ToggleButton value={HOURS_UNIT}>Hr</ToggleButton>
                  </ToggleButtonGroup>
                </Stack>
              </Stack>
            </AccordionDetails>
          </Accordion>

          <Accordion defaultExpanded sx={{ borderRadius: 2, boxShadow: 'none', border: 1, borderColor: 'divider' }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography variant="subtitle1">Audience & Messaging</Typography>
                <Typography variant="body2" color="text.secondary">
                  Tailor who sees the offer and how it is presented.
                </Typography>
              </Box>
            </AccordionSummary>
            <Divider />
            <AccordionDetails>
              <Stack spacing={2}>
                <TextField
                  label="Audience Tags"
                  fullWidth
                  value={audience}
                  onChange={(e) => setAudience(e.target.value)}
                  helperText="Comma-separated tags (e.g., family, VIP, long-stay)"
                />

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'flex-start' }}>
                  <TextField
                    label="Promotion Keywords"
                    fullWidth
                    multiline
                    minRows={2}
                    value={upsellCopy}
                    onChange={(e) => setUpsellCopy(e.target.value)}
                    helperText="Short upsell sentence shown to guests. Best under 65 characters to display fully on mobile devices."
                  />
                  <Tooltip title="Let AI suggest a high-converting upsell sentence" placement="top">
                    <span>
                      <IconButton
                        color="primary"
                        size="large"
                        onClick={handleGenerate}
                        disabled={generating || !selectedTarget}
                        sx={{ mt: { xs: 1, sm: 0.5 } }}
                      >
                        {generating ? <CircularProgress size={24} /> : <AutoAwesomeIcon />}
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
                {suggestions.length > 0 && (
                  <Stack spacing={1}>
                    <Typography variant="caption" color="text.secondary">
                      Try another variation:
                    </Typography>
                    <Stack spacing={1}>
                      {suggestions.map((copy, idx) => (
                        <Button
                          key={`${idx}-${copy.slice(0, 10)}`}
                          variant={copy === upsellCopy ? 'contained' : 'outlined'}
                          color={copy === upsellCopy ? 'primary' : 'inherit'}
                          size="small"
                          onClick={() => setUpsellCopy(copy)}
                          sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                        >
                          {copy}
                        </Button>
                      ))}
                    </Stack>
                  </Stack>
                )}

                <TextField
                  label="Notes"
                  fullWidth
                  multiline
                  minRows={2}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </Stack>
            </AccordionDetails>
          </Accordion>
        </Stack>
      </Box>
      <Divider />
      <Box sx={{ px: 3, py: 2, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
        {initialPromotion && (
          <Button color="error" onClick={handleDisable} disabled={saving}>
            Remove
          </Button>
        )}
        <Button onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {initialPromotion ? 'Save Changes' : 'Create Promotion'}
        </Button>
      </Box>
    </Drawer>
  );
}

function readableTargetType(targetType) {
  if (!targetType) return 'Unknown';
  return targetType
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (c) => c.toUpperCase())
    .trim();
}

function formatStayPhase(value) {
  if (!value) return 'Unknown';
  if (STAY_PHASE_LABELS[value]) return STAY_PHASE_LABELS[value];
  return value
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (c) => c.toUpperCase())
    .trim();
}

function optionKey(option) {
  if (!option) return '';
  return `${option.targetType}:${option.targetId}:${option.parentId || 'root'}`;
}

function buildCategoryOptions(options) {
  const availableCategories = new Set(options.map((opt) => opt.category).filter(Boolean));
  return TARGET_CATEGORY_CONFIG.filter((config) => availableCategories.has(config.value));
}

function normalizeTimeValue(minutes) {
  if (typeof minutes !== 'number' || Number.isNaN(minutes)) {
    return { value: '', unit: MINUTES_UNIT };
  }
  if (minutes >= 60 && Number.isInteger(minutes) && minutes % 60 === 0) {
    return { value: String(minutes / 60), unit: HOURS_UNIT };
  }
  return { value: String(minutes), unit: MINUTES_UNIT };
}

function formatNumberString(value) {
  if (Number.isInteger(value)) return String(value);
  return Number(value.toFixed(3)).toString();
}

function convertDisplayedValue(displayValue, fromUnit, toUnit) {
  if (!displayValue) return '';
  const numeric = Number(displayValue);
  if (!Number.isFinite(numeric)) return displayValue;
  const minutes = fromUnit === HOURS_UNIT ? numeric * 60 : numeric;
  const converted = toUnit === HOURS_UNIT ? minutes / 60 : minutes;
  return formatNumberString(converted);
}

function applyNormalizedTime(setter, unitSetter, minutes) {
  const normalized = normalizeTimeValue(minutes);
  setter(normalized.value);
  unitSetter(normalized.unit);
}
